import { activityLogger } from '@databases/logs/activity-logger';
import React, {
  createContext,
  useState,
  type PropsWithChildren,
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { type ConfirmDialogProps } from '../types';

type AllowedDialogTypes = {
  ConfirmDialog: ConfirmDialogProps;
  ExportDialog: undefined;
  ImportDialog: undefined;
  ConnectionImportOptionsDialog: undefined;
};

export type AllowedDialogKeys = keyof AllowedDialogTypes;
export type DialogDataType = AllowedDialogTypes[AllowedDialogKeys];

export type DialogsType = {
  [K in AllowedDialogKeys]?: (props: AllowedDialogTypes[K]) => React.ReactNode;
};
type ShowDialogType = <K extends AllowedDialogKeys>(
  dialog: K,
  data?: AllowedDialogTypes[K]
) => void;

export interface DialogContextInterface {
  showDialog: ShowDialogType;
  hideDialog: () => void;
}

export const DialogContext = createContext<DialogContextInterface>({
  showDialog: () => {},
  hideDialog: () => {},
});

export const DialogProvider: FC<
  PropsWithChildren<{
    defaultDialogs: DialogsType;
  }>
> = ({ children, defaultDialogs }) => {
  const [dialogs] = useState<DialogsType>(defaultDialogs);
  const [dialogComponent, setDialogComponent] = useState<React.ReactNode>(null);
  const currentDialogRef = useRef<React.ReactNode>(null);

  const showDialog: ShowDialogType = useCallback(
    <K extends AllowedDialogKeys>(dialog: K, data?: AllowedDialogTypes[K]) => {
      setDialogComponent(dialogs[dialog]?.(data as AllowedDialogTypes[K]));

      activityLogger.log('showDialog', { dialog, ...data });
    },
    [dialogs]
  );

  const hideDialog = useCallback(() => {
    setDialogComponent(null);

    activityLogger.log('hideDialog', { dialog: currentDialogRef.current });
  }, []);

  useEffect(() => {
    currentDialogRef.current = dialogComponent;
  }, [dialogComponent]);

  const value = useMemo(() => ({ showDialog, hideDialog }), [showDialog, hideDialog]);

  return (
    <DialogContext.Provider value={value}>
      {children}
      {dialogComponent}
    </DialogContext.Provider>
  );
};
